import React, { useState, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useServices } from '../context/ServiceContext';
import { useCompany } from '../context/CompanyContext';

import './ToolsPanel.css';

const TOOLS = [
  { id:'invoice',   icon:'🧾', label:'Invoice Generator',  desc:'Create & print invoices' },
  { id:'services',  icon:'🏷️', label:'Service Types',     desc:'Manage your service catalogue' },
  { id:'import',    icon:'📥', label:'Import Leads',       desc:'Upload CSV file' },
  { id:'export',    icon:'📤', label:'Export Leads',       desc:'Download as CSV' },
  { id:'exportall', icon:'💾', label:'Export All Data',    desc:'Full data backup JSON' },
  { id:'notes',     icon:'📝', label:'Quick Notes',        desc:'Scratch pad for ideas' },
];

/* ── Invoice Generator ─────────────────────────── */
function InvoiceGenerator({ theme }) {
  const { company } = useCompany();
  const [form, setForm] = useState({
    invoiceNo: 'INV-' + String(Date.now()).slice(-6),
    date: new Date().toISOString().split('T')[0],
    dueDate: '',
    clientName: '', clientCompany: '', clientAddress: '', clientEmail: '',
    service: '', description: '', amount: '', gst: '18',
  });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const subtotal = parseFloat(form.amount) || 0;
  const gstAmt   = subtotal * (parseFloat(form.gst) || 0) / 100;
  const total    = subtotal + gstAmt;
  const fmtINR   = v => '₹' + v.toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const coName    = company?.name    || 'Your Company';
  const coAddress = company?.address || '';
  const coCity    = company?.city    || '';
  const coPhone   = company?.phone   || '';
  const coEmail   = company?.email   || '';
  const coWebsite = company?.website || '';
  const coGSTIN   = company?.gstin   || '';

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>Invoice ${form.invoiceNo}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Plus Jakarta Sans',Arial,sans-serif;padding:40px;color:#1a1a1a;background:#fff}
.header{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px;padding-bottom:20px;border-bottom:2.5px solid #F5A623}
.logo-name{font-size:24px;font-weight:900;color:#0f0c08;letter-spacing:-0.02em}
.logo-tag{font-size:12px;color:#888;margin-top:3px}
.inv-badge{text-align:right}
.inv-label{font-size:11px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#F5A623;margin-bottom:4px}
.inv-num{font-size:22px;font-weight:800;color:#0f0c08}
.inv-date{font-size:12px;color:#888;margin-top:2px}
.parties{display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-bottom:32px}
.party-label{font-size:9px;font-weight:700;letter-spacing:0.12em;text-transform:uppercase;color:#aaa;margin-bottom:8px}
.party-name{font-size:16px;font-weight:700;color:#0f0c08;margin-bottom:4px}
.party-detail{font-size:12px;color:#666;line-height:1.7}
.items-table{width:100%;border-collapse:collapse;margin-bottom:24px}
.items-table th{text-align:left;padding:10px 14px;font-size:10px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;background:#f8f6f2;color:#666;border-bottom:1px solid #eee}
.items-table td{padding:14px;font-size:13px;color:#333;border-bottom:1px solid #f0f0f0}
.items-table tr:last-child td{border:none}
.items-table .amount{text-align:right;font-weight:600}
.totals{width:260px;margin-left:auto;margin-bottom:32px}
.tot-row{display:flex;justify-content:space-between;padding:8px 0;font-size:13px;border-bottom:0.5px solid #f0f0f0;color:#555}
.tot-row.final{font-size:16px;font-weight:800;color:#0f0c08;border:none;padding-top:12px}
.tot-row.final span:last-child{color:#F5A623}
.payment-box{background:#faf8f5;border-radius:10px;padding:18px;margin-bottom:32px;border:1px solid #eee}
.payment-title{font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:#F5A623;margin-bottom:12px}
.payment-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px}
.payment-item{font-size:12px;color:#666}
.payment-item strong{display:block;color:#0f0c08;font-size:13px;margin-bottom:2px}
.footer{text-align:center;font-size:11px;color:#aaa;padding-top:20px;border-top:0.5px solid #eee}
.thank-you{font-size:14px;font-weight:700;color:#0f0c08;margin-bottom:6px}
@media print{body{padding:20px}}
</style></head><body>
<div class="header">
  <div>
    <div class="logo-name">${coName}</div>
    ${company?.tagline ? `<div class="logo-tag">${company.tagline}</div>` : ''}
    <div style="margin-top:8px;font-size:12px;color:#888;line-height:1.6">
      ${coAddress ? coAddress + (coCity ? ', ' + coCity : '') + '<br>' : ''}
      ${coPhone ? coPhone + '<br>' : ''}
      ${coEmail ? coEmail : ''}
      ${coWebsite ? ' · ' + coWebsite : ''}
      ${coGSTIN ? '<br>GSTIN: ' + coGSTIN : ''}
    </div>
  </div>
  <div class="inv-badge">
    <div class="inv-label">Invoice</div>
    <div class="inv-num">${form.invoiceNo}</div>
    <div class="inv-date">Date: ${new Date(form.date).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>
    ${form.dueDate ? `<div class="inv-date" style="color:#e55">Due: ${new Date(form.dueDate).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}</div>` : ''}
  </div>
</div>
<div class="parties">
  <div>
    <div class="party-label">From</div>
    <div class="party-name">${coName}</div>
    <div class="party-detail">${coAddress || ''}<br>${coPhone || ''}<br>${coEmail || ''}</div>
  </div>
  <div>
    <div class="party-label">Bill To</div>
    <div class="party-name">${form.clientName || 'Client Name'}</div>
    <div class="party-detail">
      ${form.clientCompany ? form.clientCompany + '<br>' : ''}
      ${form.clientAddress ? form.clientAddress + '<br>' : ''}
      ${form.clientEmail ? form.clientEmail : ''}
    </div>
  </div>
</div>
<table class="items-table">
  <thead><tr><th>Description</th><th>Service</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>
    <tr>
      <td>${form.description || '—'}</td>
      <td>${form.service || '—'}</td>
      <td class="amount">${fmtINR(subtotal)}</td>
    </tr>
  </tbody>
</table>
<div class="totals">
  <div class="tot-row"><span>Subtotal</span><span>${fmtINR(subtotal)}</span></div>
  <div class="tot-row"><span>GST (${form.gst}%)</span><span>${fmtINR(gstAmt)}</span></div>
  <div class="tot-row final"><span>Total</span><span>${fmtINR(total)}</span></div>
</div>
<div class="payment-box">
  <div class="payment-title">Payment Information</div>
  <div class="payment-grid">
    <div class="payment-item"><strong>Account Name</strong>${coName}</div>
    <div class="payment-item"><strong>Bank</strong>Federal Bank</div>
    <div class="payment-item"><strong>Account No</strong>—</div>
    <div class="payment-item"><strong>IFSC</strong>—</div>
  </div>
</div>
<div class="footer">
  <div class="thank-you">Thank you for your business!</div>
  ${coName} · ${coEmail} · ${coWebsite}
</div>
</body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  return (
    <div className="tp-section">
      <div className="tp-sec-title">Invoice Generator</div>
      <div className="tp-inv-layout">
        {/* Form */}
        <div className="tp-inv-form">
          <div className="tp-inv-section-hd">Invoice Details</div>
          <div className="tp-inv-row2">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Invoice #</label><input className="tp-inv-inp" value={form.invoiceNo} onChange={e=>setF('invoiceNo',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Date</label><input className="tp-inv-inp" type="date" value={form.date} onChange={e=>setF('date',e.target.value)}/></div>
          </div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Due date (optional)</label><input className="tp-inv-inp" type="date" value={form.dueDate} onChange={e=>setF('dueDate',e.target.value)}/></div>

          <div className="tp-inv-section-hd" style={{marginTop:12}}>Bill To</div>
          <div className="tp-inv-row2">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Client name</label><input className="tp-inv-inp" placeholder="John Doe" value={form.clientName} onChange={e=>setF('clientName',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Company</label><input className="tp-inv-inp" placeholder="Company Ltd" value={form.clientCompany} onChange={e=>setF('clientCompany',e.target.value)}/></div>
          </div>
          <div className="tp-inv-row2">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Email</label><input className="tp-inv-inp" placeholder="client@email.com" value={form.clientEmail} onChange={e=>setF('clientEmail',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Address</label><input className="tp-inv-inp" placeholder="City, State" value={form.clientAddress} onChange={e=>setF('clientAddress',e.target.value)}/></div>
          </div>

          <div className="tp-inv-section-hd" style={{marginTop:12}}>Service</div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Service title</label><input className="tp-inv-inp" placeholder="e.g. Website Development" value={form.service} onChange={e=>setF('service',e.target.value)}/></div>
          <div className="tp-inv-grp"><label className="tp-inv-lbl">Description</label><input className="tp-inv-inp" placeholder="Brief description of work done" value={form.description} onChange={e=>setF('description',e.target.value)}/></div>
          <div className="tp-inv-row2">
            <div className="tp-inv-grp"><label className="tp-inv-lbl">Amount (₹)</label><input className="tp-inv-inp" type="number" placeholder="0" value={form.amount} onChange={e=>setF('amount',e.target.value)}/></div>
            <div className="tp-inv-grp"><label className="tp-inv-lbl">GST %</label><input className="tp-inv-inp" type="number" placeholder="18" value={form.gst} onChange={e=>setF('gst',e.target.value)}/></div>
          </div>
          <div className="tp-inv-total-bar">
            <span>Total</span>
            <span className="tp-inv-total-val">{fmtINR(total)}</span>
          </div>
          <div className="tp-export-btn" onClick={handlePrint}>🖨 Print / Download PDF</div>
        </div>

        {/* Live Preview */}
        <div className="tp-inv-preview" data-theme={theme}>
          <div className="tp-prev-hd">
            <div className="tp-prev-logo">{coName}</div>
            <div>
              <div className="tp-prev-inv-label">INVOICE</div>
              <div className="tp-prev-inv-num">{form.invoiceNo}</div>
              <div className="tp-prev-inv-date">{new Date(form.date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
            </div>
          </div>
          <div className="tp-prev-parties">
            <div>
              <div className="tp-prev-party-lbl">FROM</div>
              <div className="tp-prev-party-name">{coName}</div>
              <div className="tp-prev-party-det">{coEmail}</div>
            </div>
            <div>
              <div className="tp-prev-party-lbl">BILL TO</div>
              <div className="tp-prev-party-name">{form.clientName || 'Client Name'}</div>
              <div className="tp-prev-party-det">{form.clientCompany}</div>
            </div>
          </div>
          <div className="tp-prev-table">
            <div className="tp-prev-th">
              <span>Description</span><span style={{textAlign:'right'}}>Amount</span>
            </div>
            <div className="tp-prev-td">
              <div>
                <div className="tp-prev-service">{form.service || 'Service'}</div>
                <div className="tp-prev-desc">{form.description || '—'}</div>
              </div>
              <span className="tp-prev-amount">{fmtINR(subtotal)}</span>
            </div>
          </div>
          <div className="tp-prev-totals">
            <div className="tp-prev-row"><span>Subtotal</span><span>{fmtINR(subtotal)}</span></div>
            <div className="tp-prev-row"><span>GST ({form.gst}%)</span><span>{fmtINR(gstAmt)}</span></div>
            <div className="tp-prev-row tp-prev-total"><span>Total</span><span>{fmtINR(total)}</span></div>
          </div>
          <div className="tp-prev-footer">Thank you for your business!</div>
        </div>
      </div>
    </div>
  );
}

/* ── Service Types ─────────────────────────────── */
function ServiceTypes({ theme }) {
  const { services, add, remove, edit } = useServices();
  const [newSvc, setNewSvc] = useState('');
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState('');
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Service Types <span className="tp-badge">{services.length}</span></div>
      <div className="tp-svc-list">
        {services.map(s => (
          <div key={s} className="tp-svc-item">
            {editing === s ? (
              <input className="tp-svc-inp" value={editVal} autoFocus onChange={e=>setEditVal(e.target.value)}
                onBlur={()=>{edit(s,editVal);setEditing(null);}}
                onKeyDown={e=>{if(e.key==='Enter'){edit(s,editVal);setEditing(null);}if(e.key==='Escape')setEditing(null);}}/>
            ) : (
              <span className="tp-svc-name" onClick={()=>{setEditing(s);setEditVal(s);}}>{s}</span>
            )}
            <div className="tp-svc-actions">
              <div className="tp-svc-btn" onClick={()=>{setEditing(s);setEditVal(s);}}>✏</div>
              <div className="tp-svc-btn tp-svc-del" onClick={()=>remove(s)}>✕</div>
            </div>
          </div>
        ))}
      </div>
      <div className="tp-svc-add-row">
        <input className="tp-svc-add-inp" placeholder="Add new service type…" value={newSvc}
          onChange={e=>setNewSvc(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){add(newSvc);setNewSvc('');}}}/>
        <div className="tp-svc-add-btn" onClick={()=>{add(newSvc);setNewSvc('');}}>+ Add</div>
      </div>
    </div>
  );
}

function ImportLeads({ theme }) {
  const fileRef = useRef();
  const [status, setStatus] = useState('');
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setStatus(`✓ "${file.name}" selected — import ready`);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Import Leads</div>
      <div className="tp-upload-zone" onClick={()=>fileRef.current.click()}>
        <span className="tp-upload-icon">📥</span>
        <div className="tp-upload-label">Click to select CSV file</div>
        <div className="tp-upload-hint">Columns: Name, Company, Phone, Email, Source, Status</div>
        <input ref={fileRef} type="file" accept=".csv" style={{display:'none'}} onChange={handleFile}/>
      </div>
      {status && <div className="tp-status">{status}</div>}
      <a className="tp-template-link" href="data:text/csv,Name,Company,Phone,Email,Source,Status,Value%0AExample Lead,Example Corp,+91 99999 88888,example@corp.com,Website,New,50000" download="leads_template.csv">↓ Download CSV template</a>
    </div>
  );
}

function ExportLeads() {
  const handleExport = () => {
    const csv = ['Name,Company,Phone,Email,Source,Status,Value'].join('\n');
    const blob = new Blob([csv], {type:'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='leads_export.csv'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export Leads</div>
      <div className="tp-export-info">Downloads all leads as a CSV file.</div>
      <div className="tp-export-btn" onClick={handleExport}>📤 Download Leads CSV</div>
    </div>
  );
}

function ExportAll() {
  const handleExport = () => {
    const data = { exported: new Date().toISOString(), note: 'Full export from RyzenOS' };
    const blob = new Blob([JSON.stringify(data,null,2)], {type:'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='ryzenos_backup.json'; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Export All Data</div>
      <div className="tp-export-info">Full data backup including leads, invoices, team, and projects.</div>
      <div className="tp-export-btn" onClick={handleExport}>💾 Download Full Backup</div>
    </div>
  );
}

function QuickNotes() {
  const [notes, setNotes] = useState('');
  return (
    <div className="tp-section">
      <div className="tp-sec-title">Quick Notes</div>
      <textarea className="tp-notes" placeholder="Jot anything down…" value={notes} onChange={e=>setNotes(e.target.value)} rows={10}/>
      <div className="tp-notes-hint">Notes are saved in this session only.</div>
    </div>
  );
}

export default function ToolsPanel({ onClose }) {
  const { theme } = useTheme();
  const [active, setActive] = useState('invoice');

  const renderContent = () => {
    if (active==='invoice')   return <InvoiceGenerator theme={theme}/>;
    if (active==='services')  return <ServiceTypes theme={theme}/>;
    if (active==='import')    return <ImportLeads theme={theme}/>;
    if (active==='export')    return <ExportLeads/>;
    if (active==='exportall') return <ExportAll/>;
    if (active==='notes')     return <QuickNotes/>;
    return null;
  };

  return (
    <div className="tp-wrap" data-theme={theme}>
      <div className="tp-overlay" onClick={onClose}/>
      <div className="tp-panel" data-theme={theme}>
        <div className="tp-header">
          <span className="tp-title">Tools</span>
          <div className="tp-close" onClick={onClose}>✕</div>
        </div>
        <div className="tp-body">
          <div className="tp-nav">
            {TOOLS.map(t=>(
              <div key={t.id} className={`tp-nav-item${active===t.id?' active':''}`} onClick={()=>setActive(t.id)}>
                <span className="tp-nav-icon">{t.icon}</span>
                <div>
                  <div className="tp-nav-label">{t.label}</div>
                  <div className="tp-nav-desc">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="tp-content">{renderContent()}</div>
        </div>
      </div>
    </div>
  );
}
